import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlanDocumentDownload, usePlanDocumentList, PlanDocumentList, Attachment } from '@sentara-health/sentara-api-hooks-core';
import {
  Headings,
  Loader,
  Paragraph,
  Button,
  FileIcon,
  DetailedAlertMessages,
} from '@sentara-health/optimahealth_web';
import {
  errorhandler,
  PDFConverter,
  ANALYTICS_MESSAGES,
  generateCustomAdobeContentTitle,
  useAnalytics,
} from 'common';
import { GlobalContext } from '../../context';

const PlanDocuments = () => {
  // Initialize translation
  const { t } = useTranslation();

  // Fetch plan document types from API
  const { fetchPlanDocumentList } = usePlanDocumentList();
  const { fetchPlanDocument } = usePlanDocumentDownload();
  const { getFeature, loginMemberId } = useContext(GlobalContext);
  const { trackContentListing } = useAnalytics();

  // State variables for different document types
  const [affordableCareAct, setAffordableCareAct] = useState(false);
  const [benefitSummary, setBenefitSummary] = useState(false);
  const [coverageAmendment, setCoverageAmendment] = useState(false);
  const [detailedScheduleofBenefits, setDetailedScheduleofBenefits] = useState(false);
  const [legalPlanDocument, setLegalPlanDocument] = useState(false);
  const [memberGuide, setMemberGuide] = useState(false);
  const [providerTerminationLetter, setProviderTerminationLetter] =
    useState(false);
  const [summaryOfBenefitCoverage, setSummaryOfBenefitCoverage] = useState(false);

  const [planDataTerm, setPlanDataTerm] = useState<Attachment[]>([]);
  const [isPlanDoc, setIsPlanDoc] = useState(false);
  const [isTerminationListLoading, setIsTerminationListLoading] = useState(true);
  const [documentTypes, setDocumentTypes] = useState<PlanDocumentList>();

  // Loading and error handling states
  const [loader, setLoader] = useState(false);
  const [showError, setError] = useState(false);

  // Function to handle summary of benefits item click
  const fetchProviderTerminationList = useCallback(async () => {
    setLoader(true);
    const param = {
      memberId: loginMemberId,
      documentType: 'providerTerminationLetter',
      limit: 5
    };
    const result = await fetchPlanDocument(param);
    try {
      const planDataTerminationString = JSON.stringify(result?.data);
      const planDataTermination = JSON.parse(planDataTerminationString);
      setPlanDataTerm(planDataTermination);
      setIsTerminationListLoading(false); // Mark as loaded
    } catch (error) {
      console.error('Error parsing provider termination data:', error);
      setIsTerminationListLoading(false); // Even on error, stop loading
    }

    setLoader(false);
    if (result?.errorCode !== undefined) {
      errorhandler(result?.errorCode);
    }

  }, [loginMemberId, fetchPlanDocument, setPlanDataTerm, setIsTerminationListLoading, setLoader]);

  // Function to handle API response and update state
  const handleResponse = useCallback(async (response: PlanDocumentList) => {
    // Function to set state if value is true
    const setIfTrue = (
      stateFn: (value: React.SetStateAction<boolean>) => void,
      value: boolean
    ) => {
      if (value === true) {
        stateFn(true);
      }
    };

    // Update state based on API response
    setIfTrue(setAffordableCareAct, response?.affordableCareAct);
    setIfTrue(setBenefitSummary, response?.benefitSummary);
    setIfTrue(setCoverageAmendment, response?.coverageAmendment);
    setIfTrue(setDetailedScheduleofBenefits, response?.detailedScheduleofBenefits);
    setIfTrue(setLegalPlanDocument, response?.legalPlanDocument);
    setIfTrue(setSummaryOfBenefitCoverage, response?.summaryOfBenefitCoverage);
    setMemberGuide(response?.memberGuide ?? false);
    if (getFeature.ImportantMemberNotification && response?.providerTerminationLetter) {
      await fetchProviderTerminationList();
    } else {
      setIsTerminationListLoading(false);
      setLoader(false);
    }
    setIfTrue(setProviderTerminationLetter, response?.providerTerminationLetter);
  }, [
    setAffordableCareAct,
    setBenefitSummary,
    setCoverageAmendment,
    setLegalPlanDocument,
    setSummaryOfBenefitCoverage,
    setProviderTerminationLetter,
    getFeature.ImportantMemberNotification,
    fetchProviderTerminationList,
    setIsTerminationListLoading,
    setLoader
  ]);

  // Fetch plan document types and handle responses
  useEffect(() => {
    (async () => {
      if (loginMemberId) {
        setLoader(true);
        setError(false);
        const res = await fetchPlanDocumentList({ memberId: loginMemberId });
        if (res?.errorCode !== undefined) {
          setError(true);
          setLoader(false);
          errorhandler(res?.errorCode);
        } else {
          const apiResponse = res?.data;
          setDocumentTypes(apiResponse);

          if (apiResponse) {
            handleResponse(apiResponse);
          }
        }
      }
    })();
  }, [loginMemberId]);

  useEffect(() => {
    // To show Error message
    if (documentTypes && Object.keys(documentTypes).length) {
      const allFalse = Object.values(documentTypes).every(
        (value) => value === false
      );
      const onlyMemberGuideTrue = Object.entries(documentTypes).every(
        ([key, value]) =>
          key === 'memberGuide' ? value === true : value === false
      );
      if (allFalse || (onlyMemberGuideTrue && !getFeature?.MemberGuide)) {
        setError(true);
      }
      const onlyProviderTerminationTrue = Object.entries(documentTypes).every(
        ([key, value]) =>
          key === 'providerTerminationLetter' ? value === true : value === false
      );
      if (allFalse || (onlyMemberGuideTrue && !getFeature?.MemberGuide) || (onlyProviderTerminationTrue && !getFeature?.ImportantMemberNotification)) {
        setError(true);
      }
    }
  }, [documentTypes, getFeature?.ImportantMemberNotification, getFeature?.MemberGuide]);

  // Array of objects representing document types and their settings
  const planData = useMemo(
    () => [
      {
        componentShow: affordableCareAct,
        itemClickValue: 'affordableCareAct',
        iconDataTestId: 'affordableCareAct_image',
        spanDataTestId: 'affordableCareAct_form',
        tVal: 'planDocuments.affordableCareAct',
        documentType: 'affordableCareAct'
      },
      {
        componentShow: benefitSummary,
        itemClickValue: 'benefitSummary',
        iconDataTestId: 'benefitSummary_image',
        spanDataTestId: 'benefitSummary_form',
        tVal: 'planDocuments.benefitSummary',
        documentType: 'benefitSummary'
      },
      {
        componentShow: coverageAmendment,
        itemClickValue: 'coverageAmendment',
        iconDataTestId: 'coverageAmendment_image',
        spanDataTestId: 'coverageAmendment_form',
        tVal: 'planDocuments.coverageAmendment',
        documentType: 'coverageAmendment'
      },
      {
        componentShow: detailedScheduleofBenefits,
        itemClickValue: 'detailedScheduleofBenefits',
        iconDataTestId: 'detailedScheduleofBenefits_image',
        spanDataTestId: 'detailedScheduleofBenefits_form',
        tVal: 'planDocuments.detailedScheduleofBenefits',
        documentType: 'detailedScheduleofBenefits'
      },
      {
        componentShow: legalPlanDocument,
        itemClickValue: 'legalplandocument',
        iconDataTestId: 'legalPlanDocument_image',
        spanDataTestId: 'legalPlanDocument_form',
        tVal: 'planDocuments.legalPlanDocument',
        documentType: 'legalPlanDocument'
      },
      {
        componentShow: summaryOfBenefitCoverage,
        itemClickValue: 'summaryofbenefitscoverage',
        iconDataTestId: 'summaryofbenefitscoverage_image',
        spanDataTestId: 'summaryofbenefitscoverage_form',
        tVal: 'planDocuments.summaryOfBenefitCoverage',
        documentType: 'summaryOfBenefitCoverage'
      },
    ], [legalPlanDocument, affordableCareAct, coverageAmendment, summaryOfBenefitCoverage, benefitSummary, detailedScheduleofBenefits]);

  useEffect(() => {
    setIsPlanDoc(
      Object.values(planData).some((value) => value.componentShow === true)
    );
  }, [planData]);

  // Function to handle summary of benefits item click
  const handlePdfClick = async (item: string, data: any) => {
    setLoader(true);
    if (data?.errorCode !== undefined) {
      errorhandler(data?.errorCode);
    } else {
      PDFConverter(data, true, item);
    }
    setLoader(false);
  };

  // Function to handle summary of benefits item click
  const handleItemClick = async (item: string, docType: string) => {
    setLoader(true);
    const param = {
      memberId: loginMemberId,
      documentType: docType,
    };

    const res = await fetchPlanDocument(param);


    setLoader(false);
    if (res?.errorCode !== undefined) {
      errorhandler(res?.errorCode);
    } else {
      try {
        const dataString = JSON.stringify(res?.data);

        const dataParse = JSON.parse(dataString);

        PDFConverter(dataParse[0].data, true, item);
      } catch (error) {
        console.error('Error converting PDF:', error);
      }
    };
  }

  return (
    <div className="plandocuments_provider">
      {loader && <Loader />}
      {getFeature.ImportantMemberNotification && providerTerminationLetter && planDataTerm.length === 0 ? (
        <DetailedAlertMessages
          additionalInfo={t('planDocuments.customMessage')}
          collapsible
          message={t('planDocuments.title')}
          type="info"
          variant="custom"
          defaultCollapsed={true}
          className="lineBrake"
        />
      ) : null}
      {/* Display main heading */}
      <Headings
        level={1}
        text={t('common.planDocuments')}
        variant="semiBold"
        className="mb-4"
        dataTestId="plan_documents"
      />
      {/* Display error message if showError is true */}
      {showError && (
        <Paragraph
          text={t('planDocuments.error')}
          variant="regular"
          data-testid="planDocumentsError"
          className={'pe-1'}
        />
      )}
      {/* To display the Member Guide Section */}
      <div className="grid-cont plan-grid-cont d-flex mb-1">
        <div className="left-border-white" aria-hidden="true"></div>
        {memberGuide && getFeature?.MemberGuide && (
          <div className="pdf_wrapper flex-column d-flex mb-4">
            <Headings
              level={5}
              text={t('planDocuments.memberGuide')}
              variant="regular"
              dataTestId="welcome_guide_title"
              className="mb-4"
            />
            <Paragraph
              text={t('planDocuments.memberGuideMessage')}
              variant="regular"
              data-testid="memberGuideMessage"
            />
            <Button
                  dataTestId={`welcome_guide_form`}
                  onClick={() => {
                    trackContentListing(
                      'welcome_guide_form',
                      generateCustomAdobeContentTitle(
                        t('planDocuments.MG'),
                        ANALYTICS_MESSAGES.DOCUMENT_OPEN_CLICK_SUFFIX
                      ),
                      36
                    );
                    handleItemClick(t('planDocuments.MG'), 'memberguide');
                  }}
                  className={`py-4 w-100 align-items-start`}
                  iconLeft={<FileIcon className="mt-1" />}
                  variant="link_btn"
                  iconStroke="none"
                >
                  {t('planDocuments.memberGuide')}
              </Button>
          </div>
        )}

        {/* TO display the Plan document section*/}
        {memberGuide && isPlanDoc && getFeature?.MemberGuide ? (
          <>
            <div className="member_wrapper_border"></div>
            <Headings
              level={5}
              text={t('common.planDocuments')}
              dataTestId="planDocuments"
              className="mb-4"
            />
          </>
        ) : null}
        {!isTerminationListLoading && (() => {
          const visiblePlanData = planData.filter(item => item.componentShow);
          return visiblePlanData.map((item, idx) => (
            <Button
              key={item.itemClickValue}
              dataTestId={item.iconDataTestId}
              onClick={() => {
                trackContentListing(
                  item.iconDataTestId,
                  generateCustomAdobeContentTitle(
                    t(item.tVal),
                    ANALYTICS_MESSAGES.DOCUMENT_OPEN_CLICK_SUFFIX
                  ),
                  36
                );
                handleItemClick(t(item.tVal), item.documentType);
              }}
              className={`py-4 w-100 align-items-start ${idx === visiblePlanData.length - 1 ? '' : 'border-bottom rounded-0'}`}
              iconLeft={<FileIcon className="mt-1" />}
              variant="link_btn"
              iconStroke="none"
            >
              {t(item.tVal)}
            </Button>
          ));
        })()}

        {getFeature.ImportantMemberNotification && providerTerminationLetter && planDataTerm.length > 0 ? (
          <>
            {documentTypes && Object.entries(documentTypes).some(([key, value]) => key !== 'providerTerminationLetter' && value === true) ? (<div className="member_wrapper_border" />) : null}
            <Headings
              level={5}
              text={t('planDocuments.importantMemberNotifications')}
              dataTestId="ImportantDocumentsTitle"
              className="mb-4"
            />
            {planDataTerm.map((item, i) => (
              <Button
                  key={item.hash}
                  dataTestId={`planAndDocsLink_${i}`}
                  onClick={() => {
                    trackContentListing(
                      `planAndDocsLink_${i}`,
                      generateCustomAdobeContentTitle(
                        item.title ?? '',
                        ANALYTICS_MESSAGES.DOCUMENT_OPEN_CLICK_SUFFIX
                      ),
                      36
                    );
                    handlePdfClick(item.title ?? '', item.data);
                  }}
                  className={`py-4 w-100 align-items-start ${i === planDataTerm?.length - 1 ? '' : 'border-bottom rounded-0'}`}
                  iconLeft={<FileIcon className="mt-1" />}
                  variant="link_btn"
                   iconStroke="none"
                >
                  {item.title}
              </Button>               
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PlanDocuments;
